import { createSlice } from '@reduxjs/toolkit'
import {
  addMinutes,
  closestIndexTo,
  getHours,
  getMinutes,
  isAfter,
  isBefore,
  setHours,
  setMinutes,
} from 'date-fns'
import { PublishStatus } from '@apollo-types/globalTypes'

export enum PublishModeType {
  PUBLISH_NOW = 'publishNow',
  SCHEDULE_PUBLISH = 'schedulePublish',
}

export interface publishSettingState {
  baseDate: Date
  scheduledAt: Date
  publishMode: PublishModeType
  pickerMode: 'idle' | 'date' | 'time'
  pickerTimeOptions: Date[]
  validity: 'idle' | 'invalid' | 'valid'
}

const minuteOptions = ['00', '15', '30', '45']

const getPickerTimeOptions = (baseDate: Date = new Date()): Date[] => {
  const results = []
  for (let hour = 0; hour < 24; hour++) {
    for (const minute of minuteOptions) {
      const result = setHours(setMinutes(baseDate, Number(minute)), hour)
      results.push(result)
    }
  }
  return results
}

/**
 * 從 `datesArray` 取最靠近當前時間 (`baseDate`) 一小時後的時間
 */
const getDefaultScheduledAt = (baseDate: Date = new Date(), datesArray: Date[]): Date => {
  return datesArray[closestIndexTo(addMinutes(baseDate, 60), datesArray)]
}

interface GetInitialStateParams {
  scheduledAt?: Date
  baseDate: Date
  publishStatus?: PublishStatus
}

export const getInitialState = ({
  scheduledAt,
  baseDate,
  publishStatus,
}: GetInitialStateParams): publishSettingState => {
  const pickerTimeOptions = getPickerTimeOptions()
  const initialState: publishSettingState = {
    baseDate,
    scheduledAt: scheduledAt || getDefaultScheduledAt(baseDate, pickerTimeOptions),
    publishMode:
      scheduledAt && isAfter(scheduledAt, baseDate)
        ? PublishModeType.SCHEDULE_PUBLISH
        : PublishModeType.PUBLISH_NOW,
    pickerMode: 'idle',
    pickerTimeOptions,
    validity: 'idle',
  }
  if (publishStatus === PublishStatus.PUBLISHED) {
    initialState.publishMode = PublishModeType.PUBLISH_NOW
  }
  return initialState
}

export const slice = createSlice({
  name: 'publish-setting',
  initialState: getInitialState({ baseDate: new Date() }),
  reducers: {
    publishModeChanged(state, action) {
      state.publishMode = action.payload
    },
    pickerModeChanged(state, action) {
      state.pickerMode = action.payload
    },
    pickerDateChanged(state, action) {
      const [hours, minutes] = [getHours(state.scheduledAt), getMinutes(state.scheduledAt)]
      const updatedScheduledAt = setMinutes(setHours(action.payload, hours), minutes)
      state.scheduledAt = updatedScheduledAt
      state.validity = checkScheduleAtValidity(updatedScheduledAt, state.baseDate)

      state.pickerMode = 'idle'
    },
    pickerTimeChanged(state, action) {
      const [hours, minutes] = [getHours(action.payload), getMinutes(action.payload)]
      const updatedScheduledAt = setMinutes(setHours(state.scheduledAt, hours), minutes)
      state.scheduledAt = updatedScheduledAt
      state.validity = checkScheduleAtValidity(updatedScheduledAt, state.baseDate)

      state.pickerMode = 'idle'
    },
    validityChanged(state, action) {
      state.validity = action.payload
    },
  },
})

export const checkScheduleAtValidity = (scheduledAt: Date, baseDate: Date): 'valid' | 'invalid' => {
  if (isBefore(scheduledAt, addMinutes(baseDate, 5))) {
    return 'invalid'
  }
  return 'valid'
}
